import { useState, useCallback, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { httpClient } from "../../HttpClient";

// Distinct de useGmailAccount (Bureau/Mails) : consentement et token séparés
// (scope calendar.events uniquement), pour une synchro lecture + écriture
// des RDV indépendante de la connexion Gmail.
function useGoogleCalendarAccount() {
  const [connected, setConnected] = useState(false);
  const [email, setEmail] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await httpClient.get("/api/users/me/google-calendar/status");
      setConnected(data.connected);
      setEmail(data.email);
      return data.connected;
    } catch (err) {
      console.error("Erreur statut Google Calendar :", err);
      return false;
    }
  }, []);

  // Compteur de requête : en changeant de semaine rapidement, plusieurs
  // fetchEvents partent en parallèle et rien ne garantit que la dernière
  // lancée soit aussi la dernière à répondre — sans ce garde, une réponse
  // plus lente pour une semaine déjà quittée pouvait écraser les
  // événements de la semaine réellement affichée (ils semblaient alors
  // avoir "disparu").
  const fetchSeq = useRef(0);
  const fetchEvents = useCallback(async (timeMin, timeMax) => {
    const seq = ++fetchSeq.current;
    setLoadingEvents(true);
    setError(null);
    try {
      const { data } = await httpClient.get("/api/users/me/google-calendar/events", {
        params: { timeMin, timeMax },
      });
      if (seq === fetchSeq.current) setEvents(data.events || []);
    } catch (err) {
      if (seq === fetchSeq.current) setError(err.response?.data?.message || "Erreur lors du chargement des événements.");
    } finally {
      if (seq === fetchSeq.current) setLoadingEvents(false);
    }
  }, []);

  const connect = useGoogleLogin({
    flow: "auth-code",
    scope: "https://www.googleapis.com/auth/calendar.events",
    access_type: "offline",
    prompt: "consent",
    onSuccess: async (resp) => {
      setConnecting(true);
      setError(null);
      try {
        await httpClient.post("/api/users/me/google-calendar/connect", { code: resp.code });
        await refreshStatus();
      } catch (err) {
        setError(err.response?.data?.message || "Erreur lors de la connexion à Google Calendar.");
      } finally {
        setConnecting(false);
      }
    },
    onError: () => {
      setError("Échec de la connexion Google.");
      setConnecting(false);
    },
  });

  const disconnect = useCallback(async () => {
    await httpClient.delete("/api/users/me/google-calendar");
    setConnected(false);
    setEmail(null);
    setEvents([]);
  }, []);

  return {
    connected,
    email,
    events,
    loadingEvents,
    connecting,
    error,
    connect,
    refreshStatus,
    fetchEvents,
    disconnect,
  };
}

export default useGoogleCalendarAccount;
